import type { DocsAuthorizationContext } from "./docs.api";

const DOCS_ORIGIN = "https://docs.somewear.app";

export function normalizeDocsReturnPath(value: string | null): string {
	if (!value || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) {
		return "/";
	}

	try {
		const parsed = new URL(value, DOCS_ORIGIN);
		return parsed.origin === DOCS_ORIGIN
			? `${parsed.pathname}${parsed.search}${parsed.hash}`
			: "/";
	} catch {
		return "/";
	}
}

/** A form POST opens Docs without putting credentials in the URL. */
export function submitDocsHandoff(
	docsUrl: string,
	accessToken: string,
	authorizationContext: DocsAuthorizationContext,
	returnTo: string,
	origin: string,
): void {
	const base = new URL(docsUrl, origin);
	const normalizedReturnTo = normalizeDocsReturnPath(returnTo);
	const form = document.createElement("form");
	form.method = "POST";
	form.action = new URL(normalizedReturnTo, base).href;
	form.hidden = true;
	for (const [name, value] of Object.entries({
		return_to: normalizedReturnTo,
		access_token: accessToken,
		auth_context: JSON.stringify(authorizationContext),
	})) {
		const input = document.createElement("input");
		input.type = "hidden";
		input.name = name;
		input.value = value;
		form.appendChild(input);
	}
	document.body.appendChild(form);
	try {
		form.submit();
	} finally {
		form.remove();
	}
}

/** Open the site without credentials; its auth gate requests a handoff if needed. */
export function buildDocsEntryUrl(docsUrl: string, returnTo: string, appOrigin: string): string {
	const base = new URL(docsUrl, appOrigin);
	const path = normalizeDocsReturnPath(returnTo);
	return path === "/" ? base.href : new URL(path.replace(/^\//, ""), base).href;
}

/** Souvla checks the fixed Docs allowlist; only its response may choose the handoff origin. */
export function resolveDocsRedirectUrl(approvedOrigin: string): string {
	const approved = new URL(approvedOrigin);
	const isLocal = ["localhost", "127.0.0.1", "[::1]"].includes(approved.hostname);
	if (
		(approved.protocol !== "https:" && !(approved.protocol === "http:" && isLocal)) ||
		approved.username ||
		approved.password ||
		approved.search ||
		approved.hash ||
		approved.pathname !== "/"
	) {
		throw new Error("The Docs API returned an invalid origin");
	}
	return `${approved.origin}/`;
}
